import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { useBreakPoint } from '@repo/toolkit/use-break-point'
import Button from '@repo/ui/button'
import { EmailIcon, Logo, LogoutIcon, TelephoneIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { type PropsWithChildren, useEffect } from 'react'

import { useSelectedEnrollment } from '../../lib/contexts/selected-enrollment'
import useAuth from '../../lib/hooks/use-auth'
import useSidebarManager from '../../lib/hooks/use-sidebar-manager'
import { RoleEnum } from '../../lib/types/enums/users/role.enum'
import Sidebar from '../elements/sidebar'

export type SidebarItemKey =
  | 'dashboard'
  | 'staff-applications'
  | 'settings'
  | 'staff-personal-space'
  | 'components-of-stays'
  | 'cse'
  | 'cse-list-page'
  | 'ol'
  | 'ui'
  | 'personal-space'
  | 'requests'
  | 'management-of-registrants'
  | 'providers'
  | 'trips'
  | 'supervisors'
  | 'stays'
  | 'ol-centers'
  | 'ol-host-families'
  | 'ol-host-family-global-list-page'
  | 'ol-tefl-global-list-page'
  | 'ol-teaching-hall-global-list-page'
  | 'ol-coach-companies-global-list-page'
  | 'ol-tefl'
  | 'ol-teaching-halls'
  | 'ol-fees'
  | 'ol-activities'
  | 'ol-mobile-numbers-for-french-leaders'
  | 'ol-coach-companies'
  | 'staff-personal-space-personal-info'
  | 'staff-personal-space-availability'
  | 'staff-personal-space-availability-requests'
  | 'information-request-create'
  | 'information-request-list'
  | 'requests-brochures'
  | 'requests-quotations'
  | 'enrollment-quotation-create'
  | 'enrollment-quotation-list'
  | 'requests-quotations-ol'
  | 'enrollment-create'
  | 'enrollment-list'
  | 'payment-conditions-list'
  | 'enrollment-validated-list'
  | 'center-list'
  | 'center-event-list'
  | 'providers-ol-list'
  | 'providers-center-list'
  | 'ol-list-page'
  | 'ol-center-list-page'
  | 'oto-individual-place-list'
  | 'ol-rad-list-page'
  | 'providers-parameters'
  | 'ol-commissions-list-page'
  | 'trips-trip-booking'
  | 'trip-booking-list-page'
  | 'trip-dates-list-page'
  | 'primary-booked-trip-dates-list-page'
  | 'secondary-booked-trip-dates-list-page'
  | 'trips-aat'
  | 'trips-logistics'
  | 'trip-steps-list-page'
  | 'mission-list-page'
  | 'mission-list-staff'
  | 'trips-parameters'
  | 'trip-provider-list-page'
  | 'setting-travel-companies'
  | 'trip-address-list-page'
  | 'trip-booking-guideline-list-page'
  | 'transfer-list-page'
  | 'staff-applications-list-page'
  | 'pcrs-list-page'
  | 'staff-management'
  | 'staff-list-page'
  | 'staff-monitoring-list-page'
  | 'staff-request-list-page'
  | 'staff-document-list-page'
  | 'staff-trips'
  | 'journey-products'
  | 'general-settings'
  | 'site-web-settings'
  | 'client-space-settings'
  | 'staff-payments'
  | 'rad-list-page'
  | 'journey-basic-data'
  | 'journey-trips'
  | 'staff-parameters'
  | 'pcr-pay-scales-list-page'
  | 'staff-pay-scale-list-page'
  | 'contract-template-list-page'
  | 'mission-type-list-page'
  | 'setting-formula-wish-list'
  | 'setting-hobbies'
  | 'setting-staff-comment-subjects'
  | 'country-list-page'
  | 'setting-accomodation-types'
  | 'journey-place-list-page'
  | 'trips-list-page'
  | 'formula-wish-list-page'
  | 'journey-place-options-list-page'
  | 'setting-transport-means'
  | 'setting-periods'
  | 'setting-trip-places'
  | 'trip-grouping-list-page'
  | 'insurance-list-page'
  | 'products-list-page'
  | 'promo-code-list-page'
  | 'aa-keyword-list-page'
  | 'medical-term-list-page'
  | 'setting-how-did-you-hear-about-us'
  | 'can-go-out-alone-condition-list-page'
  | 'pcr-personal-space-dashboard'
  | 'pcr-personal-space-basic-statistics'
  | 'pcr-personal-space-paid-applications'
  | 'pcr-personal-space-follow-up'
  | 'ui-inputs'
  | 'journey-list-page'
  | 'trip-folder-list-page'
  | 'trips-monitoring'
  | 'mission-tracking-list-page'
  | 'staff-expense-notes-list-page'
  | 'staff-bank-details-list-page'
  | 'client-space'
  | 'guide-list-page'
  | 'youth-and-sport-number-list-page'
  | 'language-test-template-list-page'
  | 'client-personal-space-home'
  | 'client-personal-space-information'
  | 'client-personal-space-information-test-level'
  | 'client-personal-space-information-child-profile'
  | 'client-personal-space-reservations'
  | 'client-personal-space-reservations-payment'
  | 'client-personal-space-reservations-finalize-payment'
  | 'client-personal-space-reservations-cancel-journey'
  | 'enrollment-characteristics-list-page'
  | 'how-did-you-obtain-lec-brochure-list-page'
  | 'client-personal-space-information-pickup'
  | 'client-personal-space-information-identity'
  | 'allocation-budget-list-page'
  | 'client-personal-space-information-medical-statement'
  | 'client-personal-space-information-trips'
  | 'client-personal-space-information-individual-trip'
  | 'client-personal-space-information-lec-guide'
  | 'client-personal-space-trip-information'
  | 'client-personal-space-information-trip-contact'
  | 'client-personal-space-information-oto-contact'
  | 'client-personal-space-journey-preparation'
  | 'client-personal-space-departure-checklist'
  | 'staff-personal-space-contracts'
  | 'staff-personal-space-missions'
  | 'staff-personal-space-my-journeys'
  | 'staff-personal-space-my-journeys-home'
  | 'client-personal-space-administration-procedures'
  | `staff-personal-space-my-journeys-details-${string}`
  | 'accountant-missions-list-page'
  | 'travel-expenses-staff-salary-list-page'
  | 'affected-staffs-salaries-list-page'
  | 'accountant-personal-space-affected-staffs-salaries-list-page'
  | 'mission-trackings-salaries-list-page'
  | 'accountant-personal-space-mission-trackings-salaries-list-page'
  | 'accounting'
  | 'client-payments-list-page'
  | 'surveys'
  | 'end-of-stay-templates-list-page'
  | 'end-of-stay-questionnaires-processing-page'
  | 'end-of-stay-appreciations-summary-page'
  | 'providers-tarifs'
  | 'lec-bank-accounts'
  | 'center-ol-payments'
  | 'center-payments'
  | 'affected-staff-diffusions-list-page'
  | 'ol-oto-individual-places'
  | 'system-admin-users-list-page'

const displayLogoutRoles = [RoleEnum.CLIENT]

const logoOptions: Record<RoleEnum, { text: string; displayGradient?: boolean }> = {
  [RoleEnum.CLIENT]: {
    text: 'Séjours Linguistiques',
    displayGradient: false
  },
  [RoleEnum.STAFF]: {
    text: 'Orion 1.0',
    displayGradient: true
  },
  [RoleEnum.ADMIN]: {
    text: 'Orion 1.0',
    displayGradient: true
  },
  [RoleEnum.PCR]: {
    text: 'Orion 1.0',
    displayGradient: true
  },
  [RoleEnum.Ol]: {
    text: 'Orion 1.0',
    displayGradient: true
  },
  [RoleEnum.ACCOUNTANT]: {
    text: 'Orion 1.0',
    displayGradient: true
  },
  [RoleEnum.SYSTEM_ADMIN]: {
    text: 'Orion 1.0',
    displayGradient: true
  }
}

export default function MainLayout({ children }: PropsWithChildren) {
  const { user, logout } = useAuth()
  const device = useBreakPoint()
  const { clearSelectedEnrollment } = useSelectedEnrollment()
  const { isSidebarExpanded } = useSidebarManager()

  useEffect(() => {
    document.body.style.overflow = device !== 'mobile' ? 'hidden' : 'auto'
  }, [device])

  if (!user) return null
  return (
    <div className={'flex w-full flex-row gap-5 p-5 bg-primary-light h-full mobile:h-auto'}>
      <Sidebar
        logo={
          <Logo
            className="object-contain h-14"
            text={logoOptions[user.role].text}
            displayGradient={logoOptions[user.role].displayGradient}
          />
        }
        customButton={
          <div>
            {displayLogoutRoles.includes(user.role) && (
              <Button
                variant={'secondary'}
                icon={LogoutIcon}
                label={isSidebarExpanded ? 'Déconnexion' : undefined}
                className={'mt-5 py-4 w-full'}
                onClick={() => {
                  void clearSelectedEnrollment()
                  logout()
                }}
              />
            )}

            {user.role === RoleEnum.STAFF && (
              <div className="flex flex-col gap-2">
                <a
                  className="p-2 rounded bg-primary-hover flex items-center gap-1 cursor-pointer justify-center"
                  href="mailto:staff@lec.info"
                >
                  <EmailIcon width="24px" height="24px" color={tailwindColors.white.DEFAULT} />
                  <Typography
                    variant="label-medium"
                    className={cn('text-white', { hidden: !isSidebarExpanded })}
                  >
                    staff@lec.info
                  </Typography>
                </a>

                <a
                  className="p-2 rounded bg-primary-hover flex items-center gap-1 cursor-pointer justify-center"
                  href="tel:0142673300"
                >
                  <TelephoneIcon width="24px" height="24px" color={tailwindColors.white.DEFAULT} />
                  <Typography
                    variant="label-medium"
                    className={cn('text-white', { hidden: !isSidebarExpanded })}
                  >
                    01 42 67 33 00
                  </Typography>
                </a>
              </div>
            )}
          </div>
        }
      />
      <div
        id="layout-data"
        className="flex flex-1 h-full overflow-scroll overflow-x-hidden flex-col transition-all duration-300 mobile:h-[calc(100dvh-40px)]"
      >
        {children}
      </div>
    </div>
  )
}
